import {MAX_STORE_SIZE} from 'src/internal/revamp/config/createGraphQlDataClient';
import {getFieldNodesWithSpreadFragmentFields} from 'src/internal/revamp/graphql/documentUtils/entrypoint';
import ObservableFragment from 'src/internal/revamp/observable/ObservableFragment';
import ObservableQuery, {
  type PerformanceMonitorTrackFn,
} from 'src/internal/revamp/observable/ObservableQuery';
import {generateHashForDocument} from 'src/internal/revamp/queryUtils/documentHashing';
import {buildQueryAndVariablesHash} from 'src/internal/revamp/queryUtils/queryAndVariablesHashing';
import {reportRuntimeError} from 'src/internal/revamp/telemetry/internalReporting';

import type {DocumentNode, FieldNode} from 'graphql';
import type ApolloLinkExecutor from 'src/internal/revamp/apollo/ApolloLinkExecutor';
import type MemoryCache from 'src/internal/revamp/cache/MemoryCache';
import type {
  InvalidateTopLevelFieldsOptions,
  ValidTopLevelField,
} from 'src/internal/revamp/graphql/types';
import type ResultStore from 'src/internal/revamp/observable/ResultStore';
import type PollingClock from 'src/internal/revamp/polling/PollingClock';
import type {
  FragmentHashWithVariables,
  SubQueryWithMetadata,
} from 'src/internal/revamp/queryUtils/types';
import type {
  GraphQlDocument,
  VariablesOfDoc,
} from 'src/internal/shared/graphql/types/document';
import type {ObservabilityFns} from 'src/internal/shared/observability/types';

export type RootQueryParams = {
  dedupedSubQueryGroups: ReadonlyArray<SubQueryWithMetadata>;
  deduplicatedIncludeVariables: Record<string, false> | undefined;
  variables: Record<string, unknown>;
};

function evictOldest(map: Map<string, any>): void {
  const oldest = map.keys().next().value;

  if (oldest) {
    map.delete(oldest);
  }
}

export type RefetchFragmentsFn = (
  fragments: ReadonlyArray<FragmentHashWithVariables>,
) => void;

export default class ObservableStore {
  private observableQueryMap: Map<string, ObservableQuery<any>>;
  private observableFragmentMap: Map<string, ObservableFragment<any>>;

  private rootQueryRefetchDataMap: Map<
    string,
    {
      refetchFragments: RefetchFragmentsFn;
      includedFragmentHashes: ReadonlyArray<string>;
      // Ids of the useRootQuery instances currently registered against this
      // document hash. Multiple CFR components can mount the same composition
      // fragment config, so we reference-count them and only drop the entry
      // once the last one unmounts.
      instances: Set<string>;
    }
  >;

  // Stores the current params for each root query.
  // Updated during render (external store mutation is safe) so that
  // retry callbacks can read fresh params even during suspension
  // when effects don't run.
  private rootQueryParamsMap: Map<string, RootQueryParams> = new Map();

  private topLevelFieldsToObservableHashesMap: Map<string, Set<string>>;

  private apolloLinkExecutor: ApolloLinkExecutor;

  private resultStore: ResultStore;

  private observabilityFns: ObservabilityFns;

  private memoryCache: MemoryCache;

  private pollingClock: PollingClock;

  // Batching state for invalidateQueries
  private pendingTopLevelFields: Set<ValidTopLevelField> = new Set();
  private pendingPurgeTopLevelFields: Set<ValidTopLevelField> = new Set();
  private isBatchScheduled = false;

  // Batching state for refetchSingleFragment
  private pendingFragmentRefetches: Map<string, FragmentHashWithVariables> =
    new Map();

  private isFragmentBatchScheduled = false;

  constructor(
    observabilityFns: ObservabilityFns,
    apolloLinkExecutor: ApolloLinkExecutor,
    resultStore: ResultStore,
    memoryCache: MemoryCache,
    pollingClock: PollingClock,
  ) {
    this.observabilityFns = observabilityFns;
    this.apolloLinkExecutor = apolloLinkExecutor;
    this.resultStore = resultStore;
    this.memoryCache = memoryCache;
    this.pollingClock = pollingClock;

    this.observableQueryMap = new Map();
    this.observableFragmentMap = new Map();
    this.rootQueryRefetchDataMap = new Map();
    this.topLevelFieldsToObservableHashesMap = new Map();
  }

  public getObservableFragment<
    TDocumentNode extends GraphQlDocument<any, any, any, any>,
  >({
    document,
    variables,
  }: {
    document: TDocumentNode;
    variables: VariablesOfDoc<TDocumentNode> | undefined;
  }): ObservableFragment<TDocumentNode> | undefined {
    const {documentHashWithVariables} = buildQueryAndVariablesHash(
      document,
      variables,
    );

    return this.observableFragmentMap.get(documentHashWithVariables);
  }

  public getObservableQuery<
    TDocumentNode extends GraphQlDocument<any, any, any, any>,
  >({
    document,
    variables,
  }: {
    document: TDocumentNode;
    variables: VariablesOfDoc<TDocumentNode> | undefined;
  }): ObservableQuery<TDocumentNode> | undefined {
    const {documentHashWithVariables} = buildQueryAndVariablesHash(
      document,
      variables,
    );

    return this.observableQueryMap.get(documentHashWithVariables);
  }

  public createObservableFragment<
    TDocumentNode extends GraphQlDocument<any, any, any, any>,
  >({
    document,
    variables,
  }: {
    document: TDocumentNode;
    variables: VariablesOfDoc<TDocumentNode> | undefined;
  }): ObservableFragment<TDocumentNode> {
    const observable = this.getObservableFragment({document, variables});

    if (observable) {
      return observable;
    }

    const observableFragment = new ObservableFragment(
      document,
      variables,
      this.resultStore,
      this.pollingClock,
    );
    const {documentHash, documentHashWithVariables} =
      buildQueryAndVariablesHash(document, variables);

    const documentTopLevelFields =
      getFieldNodesWithSpreadFragmentFields(document);

    this.addObservableFragment(documentHashWithVariables, observableFragment);
    this.registerObservableDataOnTopLevelFields(
      documentTopLevelFields,
      documentHash,
    );
    return observableFragment;
  }

  public createObservableQuery<
    TDocumentNode extends GraphQlDocument<any, any, any, any>,
  >({
    document,
    variables,
    performanceMonitorTrack,
  }: {
    document: TDocumentNode;
    variables: VariablesOfDoc<TDocumentNode> | undefined;
    performanceMonitorTrack?: PerformanceMonitorTrackFn | null;
  }): ObservableQuery<TDocumentNode> {
    const observable = this.getObservableQuery({document, variables});

    if (observable) {
      observable.setPerformanceMonitorTrack(performanceMonitorTrack ?? null);
      return observable;
    }
    const {documentHash, documentHashWithVariables} =
      buildQueryAndVariablesHash(document, variables);

    const observableQuery = new ObservableQuery(
      document,
      variables,
      this.resultStore,
      this.pollingClock,
      this.memoryCache,
      this.apolloLinkExecutor,
      this.observabilityFns,
      performanceMonitorTrack ?? null,
    );

    const documentTopLevelFields =
      getFieldNodesWithSpreadFragmentFields(document);

    this.addObservableQuery(documentHashWithVariables, observableQuery);
    this.registerObservableDataOnTopLevelFields(
      documentTopLevelFields,
      documentHash,
    );

    return observableQuery;
  }

  public addRootQueryRefetchData(
    rootQuery: DocumentNode,
    instanceId: string,
    refetchFragments: RefetchFragmentsFn,
    includedFragmentHashes: ReadonlyArray<string>,
  ): void {
    const rootQueryHash = generateHashForDocument(rootQuery);

    const existing = this.rootQueryRefetchDataMap.get(rootQueryHash);
    if (existing) {
      // Another instance of the same composition fragment is already
      // registered. Track this one too and refresh the (last-writer-wins)
      // payload. Overwriting is safe and intended: refetchFragments is
      // document-scoped — it reads params from the document-keyed store and
      // writes to shared caches, not to any instance's React state — so any
      // registered instance's callback warms the document for all of them.
      // Refreshing also keeps a re-registering instance's callback current
      // when its own deps change while a sibling keeps the entry alive.
      existing.instances.add(instanceId);
      existing.refetchFragments = refetchFragments;
      existing.includedFragmentHashes = includedFragmentHashes;
    } else {
      this.rootQueryRefetchDataMap.set(rootQueryHash, {
        refetchFragments,
        includedFragmentHashes,
        instances: new Set([instanceId]),
      });
    }
  }

  public removeRootQueryRefetchData(
    rootQuery: DocumentNode,
    instanceId: string,
  ): void {
    const rootQueryHash = generateHashForDocument(rootQuery);

    const entry = this.rootQueryRefetchDataMap.get(rootQueryHash);
    if (!entry) {
      return;
    }

    // Only drop the entry once the last instance sharing this document
    // unmounts, so one CFR component's unmount can't deregister a sibling that
    // is still mounted (which would silently stop it refetching on
    // invalidation).
    entry.instances.delete(instanceId);
    if (entry.instances.size === 0) {
      this.rootQueryRefetchDataMap.delete(rootQueryHash);
    }
    // Note: We intentionally do NOT delete from rootQueryParamsMap here.
    // setRootQueryParams is called during render, but this cleanup runs
    // during effect cleanup which happens AFTER render. Deleting here would
    // remove params that were just set during the current render cycle.
    // The params will be overwritten on next render, so they don't accumulate.
  }

  /**
   * Stores the current params for a root query.
   * Called during render to ensure retry callbacks can read fresh params.
   * Safe to call during render as this is an external store mutation.
   */
  public setRootQueryParams(
    rootQuery: DocumentNode,
    params: RootQueryParams,
  ): void {
    const rootQueryHash = generateHashForDocument(rootQuery);
    this.rootQueryParamsMap.set(rootQueryHash, params);

    if (this.rootQueryParamsMap.size > MAX_STORE_SIZE) {
      evictOldest(this.rootQueryParamsMap);
    }
  }

  /**
   * Gets the current params for a root query.
   * Used by refetch callbacks to get fresh params instead of stale ref values.
   */
  public getRootQueryParams(
    rootQuery: DocumentNode,
  ): RootQueryParams | undefined {
    const rootQueryHash = generateHashForDocument(rootQuery);
    return this.rootQueryParamsMap.get(rootQueryHash);
  }

  public invalidateTopLevelFields(
    topLevelFields: ReadonlyArray<ValidTopLevelField>,
    options?: InvalidateTopLevelFieldsOptions,
  ): void {
    topLevelFields.forEach((field) => this.pendingTopLevelFields.add(field));

    if (options?.purgeCache) {
      topLevelFields.forEach((field) =>
        this.pendingPurgeTopLevelFields.add(field),
      );
    }

    if (!this.isBatchScheduled) {
      this.isBatchScheduled = true;
      setTimeout(() => {
        this.processPendingInvalidations();
      }, 0);
    }
  }

  private processPendingInvalidations(): void {
    if (this.pendingTopLevelFields.size === 0) {
      this.isBatchScheduled = false;
      return;
    }

    const topLevelFields = Array.from(this.pendingTopLevelFields);
    const topLevelFieldsToPurge = Array.from(this.pendingPurgeTopLevelFields);
    this.pendingTopLevelFields.clear();
    this.pendingPurgeTopLevelFields.clear();
    this.isBatchScheduled = false;

    const queryHashes =
      this.getObservableHashesForTopLevelFields(topLevelFields);

    this.resultStore.invalidateQueriesByDocumentHash(queryHashes);

    const {observableFragmentHashes, observableQueries} =
      this.zipDocumentHashesPerObservableType(queryHashes);

    const observableFragmentHashesWithVariables = observableFragmentHashes.map(
      (hash) => ({hash, variables: null}),
    );

    this.triggerFragmentsRefetch(observableFragmentHashesWithVariables);
    triggerQueriesRefetch(observableQueries);

    if (topLevelFieldsToPurge.length > 0) {
      this.memoryCache.evictTopLevelFields(topLevelFieldsToPurge);
    }
  }

  public refetchSingleFragment<
    TDocumentNode extends GraphQlDocument<any, any, any, any>,
  >({
    document,
    variables,
  }: {
    document: TDocumentNode;
    variables: VariablesOfDoc<TDocumentNode> | undefined;
  }): void {
    // undefined variables get treated as empty object down the line.
    const finalVariables = variables || {};
    const {documentHash, documentHashWithVariables} =
      buildQueryAndVariablesHash(document, finalVariables);

    const fragment = this.observableFragmentMap.get(documentHashWithVariables);
    if (!fragment) {
      reportRuntimeError(
        this.observabilityFns,
        new Error('Fragment not found'),
        'Could not find a fragment in observable store',
      );
      return;
    }

    fragment.clearLastFetchedBy();

    this.pendingFragmentRefetches.set(documentHashWithVariables, {
      hash: documentHash,
      variables: finalVariables,
    });

    if (!this.isFragmentBatchScheduled) {
      this.isFragmentBatchScheduled = true;
      setTimeout(() => {
        this.processPendingFragmentRefetches();
      }, 0);
    }
  }

  private processPendingFragmentRefetches(): void {
    if (this.pendingFragmentRefetches.size === 0) {
      this.isFragmentBatchScheduled = false;
      return;
    }

    const fragmentsToRefetch = Array.from(
      this.pendingFragmentRefetches.values(),
    );

    this.pendingFragmentRefetches.clear();
    this.isFragmentBatchScheduled = false;

    this.triggerFragmentsRefetch(fragmentsToRefetch);
  }

  private zipDocumentHashesPerObservableType(hashes: ReadonlyArray<string>): {
    observableFragmentHashes: ReadonlyArray<string>;
    observableQueries: ReadonlyArray<ObservableQuery<any>>;
  } {
    const observableFragmentHashes = hashes.filter((hash) => {
      const activeEntryIncludingHash = Array.from(
        this.observableFragmentMap.entries(),
      ).find(([key, fragment]) => {
        return key.includes(hash) && fragment.isActive();
      });

      return typeof activeEntryIncludingHash !== 'undefined';
    });

    const observableQueries = Array.from(this.observableQueryMap.entries())
      .filter(([key, query]) => {
        return hashes.some((hash) => key.includes(hash)) && query.isActive();
      })
      .map(([_key, query]) => {
        return query;
      });
    return {observableFragmentHashes, observableQueries};
  }

  private triggerFragmentsRefetch(
    fragments: ReadonlyArray<FragmentHashWithVariables>,
  ): void {
    const rootQueryRefetchCallbacks = Array.from(
      this.rootQueryRefetchDataMap.values(),
    );

    const rootQueriesToRefetch = rootQueryRefetchCallbacks.filter(
      (rootQueryData) =>
        rootQueryData.includedFragmentHashes.find((fragmentHash) =>
          fragments.some((fragment) => fragment.hash === fragmentHash),
        ),
    );

    for (const {refetchFragments} of rootQueriesToRefetch) {
      refetchFragments(fragments);
    }
  }

  private addObservableQuery<
    TDocumentNode extends GraphQlDocument<any, any, any, any>,
  >(
    documentHashWithVariables: string,
    observableData: ObservableQuery<TDocumentNode>,
  ): void {
    this.observableQueryMap.set(documentHashWithVariables, observableData);

    if (this.observableQueryMap.size > MAX_STORE_SIZE) {
      evictOldest(this.observableQueryMap);
    }
  }

  private addObservableFragment<
    TDocumentNode extends GraphQlDocument<any, any, any, any>,
  >(
    documentHashWithVariables: string,
    observableData: ObservableFragment<TDocumentNode>,
  ): void {
    this.observableFragmentMap.set(documentHashWithVariables, observableData);

    if (this.observableFragmentMap.size > MAX_STORE_SIZE) {
      evictOldest(this.observableFragmentMap);
    }
  }

  private registerObservableDataOnTopLevelFields(
    topLevelFieldNodes: ReadonlyArray<FieldNode>,
    documentHash: string,
  ): void {
    const topLevelFieldNames = topLevelFieldNodes.map(
      (field) => field.name.value,
    );

    topLevelFieldNames.forEach((fieldName) => {
      const existingSet =
        this.topLevelFieldsToObservableHashesMap.get(fieldName);
      if (typeof existingSet === 'undefined') {
        this.topLevelFieldsToObservableHashesMap.set(
          fieldName,
          new Set([documentHash]),
        );
      } else {
        existingSet.add(documentHash);
      }
    });

    if (this.topLevelFieldsToObservableHashesMap.size > MAX_STORE_SIZE) {
      evictOldest(this.topLevelFieldsToObservableHashesMap);
    }
  }

  private getObservableHashesForTopLevelFields(
    topLevelFields: ReadonlyArray<ValidTopLevelField>,
  ): ReadonlyArray<string> {
    const nonDedupedTopLevelFields = topLevelFields.flatMap((field) => {
      const existingSet = this.topLevelFieldsToObservableHashesMap.get(field);
      if (typeof existingSet === 'undefined') {
        return [];
      }

      return Array.from(existingSet);
    });

    return Array.from(new Set(nonDedupedTopLevelFields));
  }
}

function triggerQueriesRefetch(
  observableQueries: ReadonlyArray<ObservableQuery<any>>,
): void {
  observableQueries.forEach((observableQuery) => {
    observableQuery.refetch();
  });
}
